import { useFormikContext } from "formik";

import BoolCellRenderer from "components/DataGrid/customRenderers/BoolCellRenderer";
import CustomSelect from "components/Forms/CustomSelect";
import Deliverables from "features/deliverables";
import Milestone from "features/milestones/Milestone";
import MilestonePay from "features/milestones/MilestonePay";
import checkIfGracePeriod from "helpers/checkIfGracePeriod";

export const milestoneColDefs = [
  {
    headerName: "_id",
    field: "_id",
    type: "text",
    hide: true,
  },
  {
    headerName: "Name",
    field: "name",
    type: "text",
  },
  {
    headerName: "Status/Notes",
    field: "status",
    type: "text",
  },
  {
    headerName: "Rate",
    field: "rate",
    type: "currency",
  },
  {
    headerName: "Potentially Owed",
    field: "holdBalance",
    type: "currency",
  },
  // {
  //   headerName: "Available",
  //   field: "availableBalance",
  //   type: "currency",
  // },
  {
    headerName: "Earned",
    field: "paidBalance",
    type: "currency",
  },
  {
    headerName: "Reimbursement Milestone?",
    field: "overrideSegmentationSplit",
    type: "boolean",
    cellRenderer: BoolCellRenderer,
  },
  {
    headerName: "Due Date",
    field: "dueDate",
    type: "date",
  },
  {
    headerName: "Payment Terms",
    field: "paymentTerm",
    type: "text",
  },
  {
    headerName: "Next Payment Date",
    field: "nextPaymentDate",
    type: "date",
  },
  {
    headerName: "Complete?",
    field: "isComplete",
    type: "boolean",
    cellRenderer: BoolCellRenderer,
  },
  {
    headerName: "Completed Date",
    field: "dateCompleted",
    type: "date",
    // valueGetter: (params) => {
    //   return new Date(2023, 7, 0);
    // },
  },
  {
    headerName: "Release Payment?",
    field: "releasePayment",
    type: "boolean",
    cellRenderer: BoolCellRenderer,
  },
  {
    headerName: "Payment Approved?",
    field: "isPaymentApproved",
    type: "boolean",
    cellRenderer: BoolCellRenderer,
  },
  {
    headerName: "Deliverables Complete",
    field: "__deliverableMetrics.complete",
    type: "number",
  },
  {
    headerName: "Deliverables Incomplete",
    field: "__deliverableMetrics.incomplete",
    type: "number",
  },
  {
    headerName: "Deliverables Total",
    field: "__deliverableMetrics.total",
    type: "number",
  },
];

export const milestoneFields = [
  {
    fieldKey: "name",
    type: "text",
    editing: {
      required: true,
    },
    componentProps: { label: "Title" },
  },
  {
    fieldKey: "status",
    type: "text",
    editing: {
      required: false,
    },
    componentProps: { label: "Status/Notes" },
  },
  {
    fieldKey: "priority",
    type: "select",
    required: false,
    componentProps: { label: "Priority" },
    editing: {
      CustomRenderer: (props) => {
        // TODO: CustomField add type for select
        const { values } = useFormikContext();

        if (
          props.hideCondition
          && props.hideCondition(values, props.hideConditionCompareValues)
        ) {
          return null;
        }
        return (
          <CustomSelect
            {...props}
            name="priority"
            options={[
              { value: "high", label: "High" },
              { value: "normal", label: "Normal" },
              { value: "low", label: "Low" },
            ]}
          />
        );
      },
    },
  },
  {
    fieldKey: "rate",
    type: "number",
    editing: {
      required: true,
    },
    componentProps: { label: "Rate" },
  },
  {
    fieldKey: "updateAssignmentRate",
    type: "switch",
    editing: {
      required: false,
      hideCondition: (values, { assignment }) => {
        const milestonesBalance = assignment?.milestones?.reduce(
          (acc, milestone) => {
            if (milestone?._id === values?._id) return acc;
            return acc + milestone.rate;
          },
          0,
        );
        const remainingBalance = assignment?.rate - milestonesBalance;
        const shouldHide = remainingBalance >= (values?.rate || 0);
        return shouldHide;
      },
      componentProps: {
        label: "Update Assignment Rate?",
        creatorVisible: true,
      },
    },
  },
  {
    fieldKey: "overrideSegmentationSplit",
    type: "switch",
    editing: {
      required: false,
    },
    componentProps: { label: "Reimbursement Milestone?" },
  },
  {
    fieldKey: "dueDate",
    type: "date",
    editing: {
      required: true,
    },
    componentProps: { label: "Due Date" },
  },
  {
    fieldKey: "paymentTerm",
    type: "select",
    editing: {
      required: true,
      CustomRenderer: (props) => {
        const { values } = useFormikContext();

        if (
          props.hideCondition
          && props.hideCondition(values, props.hideConditionCompareValues)
        ) {
          return null;
        }
        // when changing payment term, reset next payment date value
        return (
          <CustomSelect
            {...props}
            name="paymentTerm"
            options={[
              { value: "net60", label: "Net 60" },
              { value: "net45", label: "Net 45" },
              { value: "net30", label: "Net 30" },
              { value: "other", label: "Custom" },
            ]}
          />
        );
      },
    },
    componentProps: { label: "Payment Terms" },
  },
  {
    fieldKey: "nextPaymentDate",
    type: "date",
    editing: {
      // * custom logic for conditional required can be found in form validation schema/function
      required: false,
      // * this value is calced by the backend based on the payment term and due date, unless the payment term is "other" in which case it is manually set by the user
      disableCondition: (values) => values?.paymentTerm !== "other",
      // hide if payment terms have been changed and are not "other"
      hideCondition: (values, { milestone }) =>
        // hide if changing payment terms and they are not custom/other
        (values?.paymentTerm !== milestone?.paymentTerm
          && values?.paymentTerm !== "other")
        // hide if payment terms not other, and no payment date
        || (values?.paymentTerm !== "other" && !values?.nextPaymentDate),
    },
    componentProps: { label: "Next Payment Date" },
  },
  {
    fieldKey: "isComplete",
    type: "switch",
    editing: {
      required: false,
      disableCondition: (values, initialValues) =>
        values?.releasePayment
        || (initialValues?.isPaymentApproved && values?.isPaymentApproved),
    },
    componentProps: { label: "Completed?" },
  },
  {
    fieldKey: "dateCompleted",
    type: "date",
    editing: {
      required: false,
      hideCondition: (values) => !values?.isComplete && !values?.dateCompleted,
      disableCondition: (values) =>
        values?.releasePayment || values?.isPaymentApproved,
      componentProps: { label: "Date Completed" },
    },
  },
  {
    fieldKey: "releasePayment",
    type: "switch",
    editing: {
      required: false,
      // TODO: disableCondition not working for field type switch
      disableCondition: (values, initialValues) =>
        (!initialValues?.isComplete && !values?.isComplete)
        || (initialValues?.isPaymentApproved && values?.isPaymentApproved)
        || !values?.dateCompleted,
    },
    componentProps: { label: "Release Payment?" },
  },
  {
    fieldKey: "isPaymentApproved",
    type: "switch",
    editing: {
      required: false,
      disableCondition: (values) => !values?.releasePayment,
    },
    componentProps: { label: "Payment Approved?" },
  },
  // warning for changing certain fields on historical milestones
  // if changing completion date and either new or original value are outside grace period, show warning
  {
    fieldKey: "backdateCompletedDate",
    type: "switch",
    editing: {
      required: false,
      hideCondition: (values, { milestone }) =>
        // if no date
        (!values?.dateCompleted && !milestone.dateCompleted)
        // if all dates provided are within grace period
        || ((!values.dateCompleted || checkIfGracePeriod(values.dateCompleted))
          && (!milestone.dateCompleted
            || checkIfGracePeriod(milestone.dateCompleted)))
        // no relevant fields changed
        || (values.overrideSegmentationSplit
          === milestone.overrideSegmentationSplit
          && values.rate === milestone.rate
          && values.paymentTerm === milestone.paymentTerm
          && values.dueDate === milestone.dueDate
          && values.nextPaymentDate === milestone.nextPaymentDate
          && values.isComplete === milestone.isComplete
          && values.dateCompleted === milestone.dateCompleted),
    },
    hideCondition: () => true,
    componentProps: {
      label:
        "I understand completion date is outside grace period and saving the milestone with these changes will trigger historical accounting adjustments.",
    },
  },
];

export const mileStonePaymentFields = [
  {
    // TODO: key
    fieldKey: "offPlatformPayment",
    type: "switch",
    componentProps: { label: "Off-Platform Payment?" },
    editing: true,
  },
  {
    fieldKey: "offPlatformCreated",
    type: "date",
    hideCondition: (values) => !values?.offPlatformPayment,
    editing: {
      componentProps: { label: "Payment Date" },
    },
  },
  {
    fieldKey: "offPlatformReferenceId",
    type: "text",
    hideCondition: (values) => !values?.offPlatformPayment,
    editing: {
      componentProps: { label: "Reference ID" },
    },
  },
  {
    fieldKey: "offPlatformPlatform",
    type: "text", // TODO: select
    // TODO: handle required for conditional fields
    // required: false,
    componentProps: { label: "Platform/Service" },
    hideCondition: (values) => !values?.offPlatformPayment,
    editing: true,
  },
  {
    fieldKey: "offPlatformAmount",
    type: "number", // TODO: currency input
    // TODO: handle required for conditional fields
    // required: false,
    componentProps: { label: "Amount" },
    hideCondition: (values) => !values?.offPlatformPayment,
    editing: {
      required: false,
    },
  },
  {
    fieldKey: "paymentType",
    type: "select",
    // required: false,
    hideCondition: (values) => values?.offPlatformPayment,
    componentProps: { label: "Payment Type" },
    editing: {
      // required: true,
      CustomRenderer: (props) => {
        // TODO: CustomField add type for select
        const { values } = useFormikContext();

        if (
          props.hideCondition
          && props.hideCondition(values, props.hideConditionCompareValues)
        ) {
          return null;
        }
        return (
          <CustomSelect
            {...props}
            name="paymentType"
            options={[
              { value: "full", label: "Full" },
              { value: "partial", label: "Partial" },
            ]}
          />
        );
      },
    },
  },
  {
    fieldKey: "amountPartial",
    type: "number", // TODO: currency input
    // TODO: handle required for conditional fields
    // required: false,
    creatorVisible: true,
    componentProps: { label: "Amount" },
    hideCondition: (values) =>
      values?.offPlatformPayment
      || !values?.paymentType
      || values?.paymentType === "full",
    editing: {
      required: false,
    },
  },
  {
    fieldKey: "amount",
    type: "number", // TODO: currency input
    // TODO: handle required for conditional fields
    // required: false,
    // valueGetter: (params) => console.log(params), //TODO: fix valueGetter
    creatorVisible: true,
    componentProps: { label: "Amount" },
    hideCondition: (values) =>
      values?.offPlatformPayment
      || !values?.paymentType
      || values?.paymentType === "partial",
    editing: false,
  },
  {
    fieldKey: "expectedPaymentDate",
    type: "date",
    hideCondition: (values) => values?.offPlatformPayment,
    creatorVisible: true,
    editing: {
      required: false,
    },
    componentProps: { label: "Update Expected Pay Date (Optional)" },
  },
  {
    fieldKey: "status",
    type: "text",
    hideCondition: (values) => values?.offPlatformPayment,
    creatorVisible: true,
    editing: {
      required: false,
    },
    componentProps: { label: "Update Assignment Status (Optional)" },
  },
];

export const milestonesCardTabOptions = [
  {
    label: "View/Edit",
    id: "view",
    PaneComponent: Milestone,
  },
  {
    label: "Deliverables",
    id: "deliverables",
    PaneComponent: Deliverables,
  },
  {
    label: "Pay",
    id: "pay",
    PaneComponent: MilestonePay,
  },
];
